if (!customElements.get('bundle-modal')) {
  customElements.define('bundle-modal', class BundleModal extends HTMLElement {
    constructor() {
      super();
      this.closeButton = this.querySelector('.bundle-modal__toggle');
      this.bindEvents();
    }

    bindEvents() {
      if (this.closeButton) {
        this.closeButton.addEventListener('click', this.close.bind(this));
      }
      
      // Close modal when clicking outside
      this.addEventListener('click', (event) => {
        if (event.target === this) {
          this.close();
        }
      });
      
      // Close modal with Escape key
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasAttribute('open')) {
          this.close();
        }
      });
    }

    open() {
      this.setAttribute('open', '');
      document.body.style.overflow = 'hidden';
      this.focus();
    }

    close() {
      this.removeAttribute('open');
      document.body.style.overflow = '';
      this.closeButton?.focus();
    }
  });
}

// Function to open bundle modal with dynamic content injection
function openBundleModal(productId, sectionId) {
  // Find the base modal
  const modalId = `BundleModal-${sectionId}`;
  const modal = document.getElementById(modalId);
  
  if (modal) {
    // Find the content template for this product
    const contentTemplateId = `BundleContent-${productId}`;
    const contentTemplate = document.getElementById(contentTemplateId);
    
    if (contentTemplate) {
      // Get the modal content container
      const modalContent = modal.querySelector('.bundle-modal__content');
      
      // Inject the content from the template
      modalContent.innerHTML = contentTemplate.innerHTML;
      
      // Initialize variant pickers after content injection
      initializeBundleVariantPickers(modal);
      
      // Open the modal
      modal.open();
    }
  }
}

// Function to initialize variant pickers in the bundle modal
function initializeBundleVariantPickers(modal) {
  // Find all variant pickers in the modal
  const variantPickers = modal.querySelectorAll('.bundle-product-variant-picker');
  
  variantPickers.forEach(picker => {
    // Add change event listeners to update the form data
    const selects = picker.querySelectorAll('select');
    selects.forEach(select => {
      select.addEventListener('change', function() {
        updateBundleVariantInput(picker, this);
      });
    });
  });
  
  // Add form submission listener to close modal
  const form = modal.querySelector('form[data-type="add-to-cart-form"]');
  if (form) {
    form.addEventListener('submit', function() {
      console.log('Bundle form submitted - closing modal');
      // Close the modal after a short delay to allow form submission
      setTimeout(() => {
        const modalElement = modal.closest('bundle-modal');
        if (modalElement) {
          modalElement.close();
        }
      }, 100);
    });
  }
}

// Function to update the hidden bundle variant input when a variant is selected
function updateBundleVariantInput(picker, select) {
  const productItem = picker.closest('.bundle-product-item');
  const productPrice = productItem.querySelector('.bundle-product-price');
  
  // Get the selected variant data from the picker's JSON script
  const variantsScript = picker.querySelector('script[type="application/json"]');
  if (variantsScript) {
    try {
      const variants = JSON.parse(variantsScript.textContent);
      
      // Find the variant that matches the selected options
      const selectedVariant = findVariantBySelectedOptions(picker, variants);
      
      if (selectedVariant) {
        // Update price if it changed
        if (productPrice && selectedVariant.price) {
          const formattedPrice = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(selectedVariant.price / 100);
          productPrice.textContent = formattedPrice;
        }
        
        // Rebuild the complete bundle variant IDs list
        const modal = picker.closest('bundle-modal');
        if (modal) {
          rebuildBundleVariantIdsList(modal);
        }
      }
    } catch (error) {
      console.error('Error parsing variant data:', error);
    }
  }
}

// Function to update the _bundle_variant_ids_list property
function updateBundleVariantIdsList(picker, newVariantId) {
  const modal = picker.closest('bundle-modal');
  if (!modal) return;
  
  // Get the current _bundle_variant_ids_list input
  const bundleVariantIdsInput = modal.querySelector('input[name="properties[_bundle_variant_ids_list]"]');
  if (!bundleVariantIdsInput) return;
  
  // Get the product index from the bundle-product-item
  const productItem = picker.closest('.bundle-product-item');
  const productIndex = parseInt(productItem.dataset.productIndex);
  
  if (productIndex >= 0) {
    // Get the current list of variant IDs
    let currentVariantIds = bundleVariantIdsInput.value.split(',').map(id => id.trim());
    
    // Convert the new variant ID to GID format
    const newVariantGid = `gid://shopify/ProductVariant/${newVariantId}`;
    
    // Update the variant ID at the correct position
    if (productIndex < currentVariantIds.length) {
      currentVariantIds[productIndex] = newVariantGid;
      
      // Update the input value
      bundleVariantIdsInput.value = currentVariantIds.join(',');
      
      console.log(`Updated product ${productIndex} variant ID to:`, newVariantGid);
      console.log('Updated bundle variant IDs list:', currentVariantIds.join(','));
    }
  }
}

// Function to rebuild the complete bundle variant IDs list
function rebuildBundleVariantIdsList(modal) {
  const bundleVariantIdsInput = modal.querySelector('input[name="properties[_bundle_variant_ids_list]"]');
  if (!bundleVariantIdsInput) return;
  
  // Get all bundle product items
  const bundleProductItems = modal.querySelectorAll('.bundle-product-item');
  const variantIds = [];
  
  bundleProductItems.forEach((item, index) => {
    const productHandle = item.dataset.productHandle;
    const variantPicker = item.querySelector('.bundle-product-variant-picker');
    
    if (variantPicker) {
      // This product has variants - get the selected variant
      const selectedVariant = getSelectedVariantFromPicker(variantPicker);
      if (selectedVariant) {
        variantIds.push(`gid://shopify/ProductVariant/${selectedVariant.id}`);
      } else {
        // Fallback to first available variant
        const variantsScript = variantPicker.querySelector('script[type="application/json"]');
        if (variantsScript) {
          try {
            const variants = JSON.parse(variantsScript.textContent);
            if (variants.length > 0) {
              variantIds.push(`gid://shopify/ProductVariant/${variants[0].id}`);
            }
          } catch (error) {
            console.error('Error parsing variants:', error);
          }
        }
      }
    } else {
      // This product has no variants - get the default variant from the product data
      const productData = getProductDataFromItem(item);
      if (productData && productData.selected_variant_id) {
        variantIds.push(`gid://shopify/ProductVariant/${productData.selected_variant_id}`);
      } else if (productData && productData.variants && productData.variants.length > 0) {
        variantIds.push(`gid://shopify/ProductVariant/${productData.variants[0].id}`);
      }
    }
  });
  
  // Update the input with the complete list
  bundleVariantIdsInput.value = variantIds.join(',');
  console.log('Rebuilt bundle variant IDs list:', variantIds.join(','));
}

// Helper function to get selected variant from picker
function getSelectedVariantFromPicker(picker) {
  const variantsScript = picker.querySelector('script[type="application/json"]');
  if (!variantsScript) return null;
  
  try {
    const variants = JSON.parse(variantsScript.textContent);
    const selects = picker.querySelectorAll('select');
    const selectedOptions = {};
    
    // Collect all selected option values
    selects.forEach(select => {
      const optionName = select.name.replace('options[', '').replace(']', '');
      selectedOptions[optionName] = select.value;
    });
    
    // Find the variant that matches all selected options
    return variants.find(variant => {
      if (selectedOptions['Size'] && variant.option1 !== selectedOptions['Size']) return false;
      if (selectedOptions['Color'] && variant.option2 !== selectedOptions['Color']) return false;
      if (selectedOptions['Material'] && variant.option3 !== selectedOptions['Material']) return false;
      return true;
    });
  } catch (error) {
    console.error('Error getting selected variant:', error);
    return null;
  }
}

// Helper function to get product data from item (for products without variant pickers)
function getProductDataFromItem(item) {
  const productDataScript = item.querySelector('script.bundle-product-data');
  if (productDataScript) {
    try {
      return JSON.parse(productDataScript.textContent);
    } catch (error) {
      console.error('Error parsing product data:', error);
      return null;
    }
  }
  return null;
}

// Function to find the variant that matches the selected options
function findVariantBySelectedOptions(picker, variants) {
  const selects = picker.querySelectorAll('select');
  const selectedOptions = {};
  
  // Collect all selected option values
  selects.forEach(select => {
    const optionName = select.name.replace('options[', '').replace(']', '');
    selectedOptions[optionName] = select.value;
  });
  
  // Find the variant that matches all selected options
  return variants.find(variant => {
    if (selectedOptions['Size'] && variant.option1 !== selectedOptions['Size']) return false;
    if (selectedOptions['Color'] && variant.option2 !== selectedOptions['Color']) return false;
    if (selectedOptions['Material'] && variant.option3 !== selectedOptions['Material']) return false;
    return true;
  });
}
